import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Building2, Mail, Phone, MapPin, Calendar, Globe, Linkedin,
  Facebook, Instagram, FileText, Edit3, Loader2, ExternalLink,
  Shield, CheckCircle, Briefcase, ArrowLeft, LayoutDashboard,
  Users
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { getWilayas, getCommunesOfWilaya } from "../../utils/Api";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:4000/api";

const Profile = () => {
  const navigate = useNavigate();
  const { user, token, loading } = useSelector((state) => state.auth);
  const isAuthenticated = !!token && !!user;

  const [recruiterData, setRecruiterData] = useState(null);
  const [resolvedAddress, setResolvedAddress] = useState({ city: '', wilaya: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [coverImageUrl, setCoverImageUrl] = useState(null); // Added for cover photo
  const [isUploadingCover, setIsUploadingCover] = useState(false); // Loading state for cover upload

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!isAuthenticated) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/recruiter/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          if (response.status === 404) {
            setError("Aucun profil recruteur trouvé.");
            setRecruiterData(null);
          } else {
            const errorData = await response.json();
            throw new Error(errorData.error || `Erreur ${response.status}`);
          }
        } else {
          const profileData = await response.json();
          if (profileData.recruiter) {
            const data = {
              user: {
                email: user.email,
                role: user.role,
                is_active: user.is_active,
                is_email_verified: user.is_email_verified,
                created_at: user.created_at,
              },
              recruiter: { ...profileData.recruiter },
            };
            setRecruiterData(data);

            // Resolve Address Names
            if (data.recruiter.address) {
              let cityName = data.recruiter.address.city;
              let wilayaName = data.recruiter.address.wilaya;

              if (data.recruiter.address.wilaya) {
                try {
                  const wilayaRes = await getWilayas(data.recruiter.address.wilaya);
                  const matchedWilaya = wilayaRes.find(w =>
                    w.id === data.recruiter.address.wilaya ||
                    w.code === data.recruiter.address.wilaya ||
                    w.name_fr.toLowerCase() === data.recruiter.address.wilaya.toLowerCase() ||
                    w.name_ar === data.recruiter.address.wilaya ||
                    (w.name && w.name.toLowerCase() === data.recruiter.address.wilaya.toLowerCase())
                  );

                  if (matchedWilaya) {
                    wilayaName = matchedWilaya.name_fr || matchedWilaya.name;

                    if (data.recruiter.address.city) {
                      try {
                        const communesRes = await getCommunesOfWilaya(matchedWilaya.code);
                        const matchedCommune = communesRes.find(c =>
                          c.id === data.recruiter.address.city ||
                          c.name.toLowerCase() === data.recruiter.address.city.toLowerCase()
                        );
                        if (matchedCommune) {
                          cityName = matchedCommune.name;
                        }
                      } catch (cErr) {
                        console.error('Error fetching communes:', cErr);
                      }
                    }
                  }
                } catch (wErr) {
                  console.error('Error fetching wilayas:', wErr);
                }
              }
              setResolvedAddress({ city: cityName, wilaya: wilayaName });
            }

            // Profile Picture Logic
            if (data.recruiter.profile_picture) {
              if (data.recruiter.profile_picture.startsWith('http')) {
                setProfileImageUrl(data.recruiter.profile_picture);
              } else {
                fetchImage(data.recruiter.profile_picture, setProfileImageUrl);
              }
            }

            // Cover Picture Logic
            if (data.recruiter.cover_picture) {
              fetchImage(data.recruiter.cover_picture, setCoverImageUrl);
            }

          } else {
            setError("Profil non trouvé.");
            setRecruiterData(null);
          }
        }
      } catch (err) {
        console.error("Erreur fetchProfileData:", err);
        setError("Une erreur est survenue lors du chargement du profil.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, [user?.id, isAuthenticated, token]);


  const fetchImage = async (pathString, setter) => {
    try {
      const pathParts = pathString.split('/');
      const filename = pathParts.pop();
      const folder = pathParts.length > 0 ? pathParts.join('/') : 'profile_pictures'; // Default fallback, though expected 'covers' for covers

      const response = await api.get(`/files/${folder}/${filename}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const objectUrl = window.URL.createObjectURL(blob);
      setter(objectUrl);
    } catch (err) {
      console.error("Error fetching image blob", err);
    }
  }



  const handleCoverUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert("La taille de l'image ne doit pas dépasser 5 Mo.");
      return;
    }

    const formData = new FormData();
    formData.append("cover_picture", file);

    setIsUploadingCover(true);
    try {
      const response = await api.post("/recruiter/profile/upload-cover", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.recruiter) {
        // Update local data
        const updatedRecruiter = response.data.recruiter;
        setRecruiterData(prev => ({ ...prev, recruiter: updatedRecruiter }));

        // Fetch and display new image
        if (updatedRecruiter.cover_picture) {
          fetchImage(updatedRecruiter.cover_picture, setCoverImageUrl);
        }
      }
    } catch (error) {
      console.error("Error uploading cover:", error);
      alert("Erreur lors du téléchargement de la photo de couverture.");
    } finally {
      setIsUploadingCover(false);
    }
  };

  const handleDeleteCover = async () => {
    if (!window.confirm("Voulez-vous vraiment supprimer votre photo de couverture ?")) return;

    setIsUploadingCover(true); // Reuse loading state
    try {
      const response = await api.delete("/recruiter/profile/cover");
      if (response.data.recruiter) {
        setRecruiterData(prev => ({ ...prev, recruiter: response.data.recruiter }));
        setCoverImageUrl(null);
      }
    } catch (error) {
      console.error("Error deleting cover:", error);
      alert("Erreur lors de la suppression.");
    } finally {
      setIsUploadingCover(false);
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getSocialIcon = (platform) => {
    switch (platform) {
      case "linkedin": return <Linkedin className="w-5 h-5" />;
      case "facebook": return <Facebook className="w-5 h-5" />;
      case "instagram": return <Instagram className="w-5 h-5" />;
      default: return <ExternalLink className="w-5 h-5" />;
    }
  };

  if (isLoading || loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-gray-200" />
      </div>
    );
  }

  if (error || !recruiterData) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
        <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 p-8 text-center shadow-md border border-gray-200 dark:border-gray-800">
          <Shield className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">Profil inaccessible</h3>
          <p className="mb-6 text-gray-500 dark:text-gray-400">{error || "Profil non configuré."}</p>
          <button onClick={() => navigate('/dashboard/settings')} className="rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700">
            Configurer
          </button>
        </div>
      </div>
    );
  }

  const { recruiter } = recruiterData;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Navbar with Back Button */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Retour</span>
          </button>
          <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Mon Profil</h1>
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>
      </div>

      <div className="pb-12">
        {/* Banner with Cover Photo */}
        <div className="h-48 w-full bg-gradient-to-r from-gray-700 to-gray-900 overflow-hidden relative group">
          {coverImageUrl ? (
            <img src={coverImageUrl} alt="Cover" className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-black/10"></div>
          )}

          {/* Cover Photo Actions */}
          <div className="absolute bottom-4 right-4 flex gap-2">
            <label className="cursor-pointer p-2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-800 rounded-full shadow-sm backdrop-blur-sm transition-all text-gray-700 dark:text-gray-200">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleCoverUpload}
                disabled={isUploadingCover}
              />
              {isUploadingCover ? <Loader2 className="w-5 h-5 animate-spin" /> : <Edit3 className="w-5 h-5" />}
            </label>

            {coverImageUrl && (
              <button
                onClick={handleDeleteCover}
                disabled={isUploadingCover}
                className="p-2 bg-red-500/90 hover:bg-red-600 rounded-full shadow-sm backdrop-blur-sm transition-all text-white"
              >
                <div className="w-5 h-5 flex items-center justify-center font-bold">×</div>
              </button>
            )}
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="relative -mt-16 mb-8 pb-6 border-b border-gray-200 dark:border-gray-800">
            <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
              {/* Profile Image */}
              <div className="relative">
                <div className="h-32 w-32 rounded-lg bg-white dark:bg-gray-900 p-1 shadow-md border border-gray-200 dark:border-gray-800">
                  <img
                    src={profileImageUrl || `https://ui-avatars.io/api/?name=${recruiter.company_name}&background=f3f4f6&color=374151&size=128&bold=true`}
                    alt={recruiter.company_name}
                    className="h-full w-full object-cover rounded-md bg-gray-50 dark:bg-gray-800"
                  />
                </div>
              </div>

              {/* Info & Actions */}
              <div className="flex-1 w-full pt-2 md:pt-0 mt-24">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h1 className=" text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      {recruiter.company_name}
                    </h1>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-2 text-gray-600 dark:text-gray-400">
                      {(recruiter.sector || recruiter.custom_sector) && (
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <span>{recruiter.sector?.name || recruiter.custom_sector}</span>
                        </div>
                      )}
                      {(resolvedAddress.wilaya || resolvedAddress.city) && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <span>{resolvedAddress.city ? `${resolvedAddress.city}, ` : ''}{resolvedAddress.wilaya}</span>
                        </div>
                      )}
                      {recruiter.website && (
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <a href={recruiter.website.startsWith('http') ? recruiter.website : `https://${recruiter.website}`} target="_blank" rel="noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">
                            Site Web
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Edit Button */}
                  <button
                    onClick={() => navigate('/dashboard/settings')}
                    className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
                  >
                    <Edit3 className="h-4 w-4" />
                    <span>Modifier</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: About & Description */}
            <div className="lg:col-span-2 space-y-8">
              {/* About Section */}
              <section className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">À propos de l'entreprise</h2>
                <div className="prose prose-slate dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                  {recruiter.description ? (
                    <p className="whitespace-pre-line">{recruiter.description}</p>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 dark:bg-gray-800/50 rounded-md border border-dashed border-gray-300 dark:border-gray-700">
                      <p className="text-gray-500 dark:text-gray-400">Aucune description renseignée.</p>
                      <button onClick={() => navigate('/dashboard/settings')} className="text-primary-600 dark:text-primary-400 font-medium hover:underline mt-1">Ajouter une description</button>
                    </div>
                  )}
                </div>
              </section>

              {/* Sector Section */}
              {(recruiter.sector || recruiter.custom_sector) && (
                <section className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Secteur d'activité</h2>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-100 dark:border-gray-800">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{recruiter.sector?.name || recruiter.custom_sector}</h3>
                    {recruiter.sector?.description && (
                      <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">{recruiter.sector.description}</p>
                    )}
                  </div>
                </section>
              )}
            </div>

            {/* Right Column: Sidebar */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Coordonnées</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500 mt-0.5" />
                    <div className="overflow-hidden">
                      <span className="block text-sm font-medium text-gray-900 dark:text-white">{recruiterData.user.email}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Email professionnel</span>
                    </div>
                  </div>

                  {recruiter.phone_number && (
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-gray-400 dark:text-gray-500 mt-0.5" />
                      <div>
                        <span className="block text-sm font-medium text-gray-900 dark:text-white">{recruiter.phone_number}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Téléphone</span>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500 mt-0.5" />
                    <div>
                      <span className="block text-sm font-medium text-gray-900 dark:text-white">{formatDate(recruiterData.user.created_at)}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Membre depuis</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              {recruiter.social_links && Object.values(recruiter.social_links).some(l => l) && (
                <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Réseaux Sociaux</h3>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(recruiter.social_links).map(([key, url]) => {
                      if (!url) return null;
                      return (
                        <a
                          key={key}
                          href={url.startsWith('http') ? url : `https://${url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        >
                          {getSocialIcon(key)}
                        </a>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
